import type { EChartsOption, LegendComponentOption } from 'echarts';

import { withOpacity } from 'components/Studio/chartStyle';
import abbreviateNumber from 'utils/abbreviateNumber';

export type FormatMetric = (args: {
  metricId: string;
  value: string | number | null | undefined;
}) => string;

export const FONT_SIZE = 11;

export const escapeHtml = (text: string) =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

// Value axis labels, abbreviated as the Highcharts charts' are (12.5k).
// Others drop the float noise a computed tick can carry
// (10.120000000000001).
export const formatAxisValue = (value: number) => {
  const abbreviated = abbreviateNumber(value);
  return abbreviated
    ? abbreviated.abbreviated.toLowerCase()
    : `${Number(value.toPrecision(12))}`;
};

// A value label as the Highcharts charts draw it: abbreviated, in the
// metric's format
export const formatValueLabel = ({
  metricId,
  value,
  formatMetric,
}: {
  metricId: string;
  value: unknown;
  formatMetric: FormatMetric;
}) => {
  const abbreviated = abbreviateNumber(value);
  return formatMetric({
    metricId,
    value: abbreviated ? abbreviated.abbreviated : (value as number | null),
  }).trim();
};

// Value labels drawn over the chart. In dark mode they're ringed in the
// background's colour, as the Highcharts charts' are, so they read over
// bars and lines.
export const toValueLabelStyle = (theme: StudioCharts.Theme) => ({
  fontSize: FONT_SIZE,
  fontWeight: 'bold' as const,
  color: theme.textPrimary,
  ...(theme.labelHalo
    ? { textBorderColor: theme.labelHalo, textBorderWidth: 3 }
    : {}),
});

// A linear gradient from the colour at one opacity to another, top to
// bottom or, for horizontal bars, right to left
export const toGradient = ({
  color,
  from,
  to,
  isHorizontal = false,
}: {
  color: string;
  from: number;
  to: number;
  isHorizontal?: boolean;
}) => ({
  type: 'linear' as const,
  x: isHorizontal ? 1 : 0,
  y: 0,
  x2: 0,
  y2: isHorizontal ? 0 : 1,
  colorStops: [
    { offset: 0, color: withOpacity({ color, opacity: from }) },
    { offset: 1, color: withOpacity({ color, opacity: to }) },
  ],
});

// How every chart's tooltip looks: outside the block, so the card doesn't
// clip it
export const toTooltipStyle = (theme: StudioCharts.Theme) => ({
  appendTo: 'body',
  backgroundColor: theme.tooltipBackground,
  borderColor: theme.tooltipBorder,
  borderWidth: 1,
  borderRadius: 8,
  padding: 12,
  textStyle: {
    color: theme.tooltipText,
    fontFamily: theme.fontFamily,
    fontSize: 12,
  },
});

// A tooltip's heading and rows, escaped. A row's note (e.g. a forecast's
// range) follows it, fainter.
export const toTooltipHtml = ({
  title,
  rows,
}: {
  title: string;
  rows: { marker?: string; name: string; value: string; note?: string }[];
}) =>
  [
    `<b>${escapeHtml(title)}</b>`,
    ...rows.map(
      (row) =>
        `${row.marker ?? ''}${escapeHtml(row.name)}: <b>${escapeHtml(
          row.value,
        )}</b>${
          row.note
            ? `<br/><span style="color: var(--color-tooltip-text-muted)">${escapeHtml(
                row.note,
              )}</span>`
            : ''
        }`,
    ),
  ].join('<br/>');

// The legend under the chart: every series, or only those named. With
// icons (one per name, e.g. a Pictorial Bar's), each entry is drawn with its
// own, in a wider box so wide icons aren't shrunk to a dot.
export const toLegend = ({
  show,
  theme,
  names,
  icons,
}: {
  show: boolean;
  theme: StudioCharts.Theme;
  names?: string[];
  icons?: string[];
}): LegendComponentOption => ({
  show,
  ...(names
    ? {
        data: icons
          ? names.map((name, index) => ({ name, icon: icons[index] }))
          : names,
      }
    : {}),
  bottom: 0,
  left: 0,
  icon: 'circle',
  itemWidth: icons ? 16 : 8,
  itemHeight: icons ? 10 : 8,
  textStyle: { color: theme.textPrimary, fontSize: FONT_SIZE },
});

// What every chart's options start from
export const toBaseOption = (theme: StudioCharts.Theme): EChartsOption => ({
  animationDuration: 300,
  animationDurationUpdate: 300,
  textStyle: {
    fontFamily: theme.fontFamily,
    fontSize: FONT_SIZE,
    color: theme.textPrimary,
  },
});

export { withOpacity };
